import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import UCPageShell from "../../../components/ui/UCPageShell";
import SectionTitle from "../../../components/shared/SectionTitle";
import UCTable from "../../../components/ui/UCTable";
import AceAvatar from "../../../components/shared/AceAvatar";
import { fetchUsersClassesOverview } from "../../../services/usersClassesService";
import {
  getAssignedClasses,
  getCurrentUser,
  isAdminUser,
} from "../../../utils/auth";

function normalizeClass(value) {
  return String(value || "").trim().toLowerCase();
}

function scopeOverview(data) {
  const currentUser = getCurrentUser();

  if (isAdminUser(currentUser)) {
    return data || { users: [], classes: [] };
  }

  const allowed = new Set(
    (getAssignedClasses(currentUser) || []).map(normalizeClass)
  );

  const users = (data?.users || []).filter((user) =>
    allowed.has(normalizeClass(user?.className))
  );

  const classes = (data?.classes || []).filter((klass) =>
    allowed.has(normalizeClass(klass?.name || klass?.className || klass?.id))
  );

  return { users, classes };
}

function StatusDot({ status, tone }) {
  const toneClass =
    tone === "warn"
      ? "is-warn"
      : tone === "soft"
      ? "is-soft"
      : "is-ok";

  return (
    <div className="ac-uc-statuscell">
      <span className={`ac-uc-statusdot ${toneClass}`} aria-hidden="true" />
      <span className="ac-uc-statuslabel">{status}</span>
    </div>
  );
}

function StreakSummary({ activeCount, bestStreak }) {
  return (
    <div className="ac-uc-streaksummary">
      <div className="ac-uc-streaksummary-main">{activeCount} aktiv</div>
      <div className="ac-uc-streaksummary-sub">beste Streak: {bestStreak} Tage</div>
    </div>
  );
}

function TeacherAccessCell({ row }) {
  if (!row.teacherAssigned) {
    return (
      <div className="ac-uc-streaksummary">
        <div className="ac-uc-streaksummary-main">Kein Zugang</div>
        <div className="ac-uc-streaksummary-sub">
          noch keine Lehrkraft verknüpft
        </div>
      </div>
    );
  }

  const isActive = row.teacherSessionActive;

  return (
    <div className="ac-uc-streaksummary">
      <div className="ac-uc-streaksummary-main">{row.teacherNames}</div>
      <div className="ac-uc-streaksummary-sub">
        {isActive
          ? `Aktiv · ${row.teacherInfo || "gerade online"}`
          : `Freigegeben`}
      </div>
    </div>
  );
}

export default function ClassesListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [overview, setOverview] = useState({ users: [], classes: [] });
  const [loading, setLoading] = useState(true);

  const backTo =
    location.state?.backTo || (location.state?.fromStats ? "/stats" : "/users-classes");

  const backLabel =
    location.state?.backLabel ||
    (location.state?.fromStats ? "← Zurück zu Statistiken" : "← Übersicht");

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const data = await fetchUsersClassesOverview();
        if (!mounted) return;
        setOverview(scopeOverview(data));
      } catch (error) {
        console.error("Classes konnten nicht geladen werden:", error);
        if (!mounted) return;
        setOverview({ users: [], classes: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const rows = useMemo(() => {
    const query = q.trim().toLowerCase();

    return overview.classes
      .filter((c) =>
        !query
          ? true
          : `${c.name} ${c.school} ${c.teacherNames || ""}`.toLowerCase().includes(query)
      )
      .map((c) => ({
        ...c,
      }));
  }, [overview.classes, q]);

  const overviewStats = useMemo(() => {
    return {
      totalStudents: rows.reduce((sum, row) => sum + Number(row.total || 0), 0),
      activeStreaks: rows.reduce((sum, row) => sum + Number(row.activeStreaks || 0), 0),
      criticalClasses: rows.filter((row) => row.elevated >= 2).length,
      activeTeachers: rows.filter((row) => row.teacherSessionActive).length,
    };
  }, [rows]);

  const cols = useMemo(
    () => [
      {
        key: "name",
        label: "Klasse",
        render: (c) => (
          <div className="ac-uc-personcell">
            <div className="ac-uc-personname">{c.name}</div>
            <div className="ac-uc-personmeta">{c.school}</div>
          </div>
        ),
      },
      {
        key: "total",
        label: "Schüler:innen",
        render: (c) => <span className="ac-uc-numbercell">{c.total}</span>,
      },
      {
        key: "teacherAccess",
        label: "Lehrkraft-Zugang",
        render: (c) => <TeacherAccessCell row={c} />,
      },
      {
        key: "streaks",
        label: "Streaks",
        render: (c) => (
          <StreakSummary activeCount={c.activeStreaks} bestStreak={c.bestStreak} />
        ),
      },
      {
        key: "status",
        label: "Status",
        render: (c) => <StatusDot status={c.status} tone={c.statusTone} />,
      },
      {
        key: "action",
        label: "Aktion",
        render: (c) => (
          <button
            onClick={() =>
              navigate(`/users-classes/classes/${c.id}`, {
                state: {
                  ...location.state,
                  backTo,
                  backLabel,
                },
              })
            }
            className="ac-ghostbtn ac-uc-tablebtn"
            type="button"
          >
            Details
          </button>
        ),
      },
    ],
    [navigate, location.state, backTo, backLabel]
  );

  return (
    <UCPageShell
      title="Klassen"
      right={
        <button onClick={() => navigate(backTo)} className="ac-ghostbtn" type="button">
          {backLabel}
        </button>
      }
    >
      <div className="space-y-5">
        <section className="ac-soft-panel ac-uc-hero">
          <div className="ac-uc-hero-grid">
            <div>
              <h2 className="ac-uc-hero-title">Klassen</h2>
              <p className="ac-uc-hero-text">
                Hier findest du eine Übersicht aller Klassen und erkennst Belastung,
                Aktivität, Nutzung und verknüpfte Lehrkraft-Zugänge schneller im gemeinsamen
                Kontext.
              </p>

              <div className="ac-uc-chip-row">
                <div className="ac-uc-chip">
                  <span className="ac-uc-chip-value">{loading ? "…" : rows.length}</span>
                  <span className="ac-uc-chip-label">Treffer</span>
                </div>
                <div className="ac-uc-chip">
                  <span className="ac-uc-chip-value">{loading ? "…" : overviewStats.totalStudents}</span>
                  <span className="ac-uc-chip-label">Schüler:innen</span>
                </div>
                <div className="ac-uc-chip">
                  <span className="ac-uc-chip-value">{loading ? "…" : overviewStats.activeStreaks}</span>
                  <span className="ac-uc-chip-label">aktive Streaks</span>
                </div>
                <div className="ac-uc-chip">
                  <span className="ac-uc-chip-value">{loading ? "…" : overviewStats.activeTeachers}</span>
                  <span className="ac-uc-chip-label">aktive Lehrkräfte</span>
                </div>
              </div>

              <div className="ac-uc-filtergrid ac-uc-filtergrid-single">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Suche: 4AD, Prof. Müller…"
                  className="ac-uc-input"
                />
              </div>
            </div>

            <div className="ac-uc-hero-side">
              <div className="ac-uc-acebox">
                <AceAvatar variant="motiviert1" size={56} />
                <div className="ac-uc-acebubble">
                  Ace sagt: In der Klassenansicht erkennst du direkt, ob eine Klasse
                  schon mit einer Lehrkraft verknüpft ist und ob der Zugang aktiv genutzt wird.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-soft-panel ac-uc-block">
          <SectionTitle title="Klassen-Liste" subtitle={`Treffer: ${rows.length}`} />

          <div className="mt-4 ac-classes-table-scope">
            {loading ? (
              <div className="ac-uc-empty">Lade Klassen…</div>
            ) : rows.length === 0 ? (
              <div className="ac-uc-empty">Kein Treffer. Versuche einen anderen Suchbegriff.</div>
            ) : (
              <UCTable columns={cols} rows={rows} rowKey={(c) => c.id} />
            )}
          </div>
        </section>
      </div>
    </UCPageShell>
  );
}
